import { useCallback } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteUser, GetUsers } from '../api';
import { usersQueryKeys } from '../data';
import {
  changeUsersPage,
  useUsersFilter,
  useUsersPagination,
  useUsersRowsPerPage,
} from '../store';

import { useUsersCurrentPage } from './use-users-current-page';
import { useResetUserQueries } from './use-reset-user-queries';

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const resetUserQueries = useResetUserQueries();
  const filters = useUsersFilter();
  const pagination = useUsersPagination();
  const page = useUsersCurrentPage();
  const rowsPerPage = useUsersRowsPerPage();

  const handleChangePage = useCallback(() => {
    const data = queryClient.getQueryData<GetUsers['return']>([
      usersQueryKeys.getUsers,
      { ...filters, ...pagination },
    ]);
    if (!data?.pagination.totalItems) return;

    const isLastItem =
      data.pagination.totalItems - (page - 1) * rowsPerPage === 1;

    if (isLastItem && page > 1) return changeUsersPage(page - 1);
  }, [filters, page, pagination, queryClient, rowsPerPage]);

  const mutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      resetUserQueries();
      handleChangePage();
    },
  });

  return mutation;
};
